import React from "react";
import ReactDOM from "react-dom";
import {
  IconButton,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { saveGridColumnState } from '@dataGrid/gridUtilities';
import Tooltip from "../../components/common/StyledToolTip";
import ListSearch from "../../components/common/MUI/ListSearch";

const styles = (theme) => ({
  root: {
    display: "block",
    float: "left",
  },
  paper: {
    backgroundColor: theme.ModalBGColor,
    padding: 8
  },
  menuList: {
    maxHeight: "284px",
    overflow: "auto",
  },
  menuItemText: {
    fontSize: theme.textFontSize,
    padding: 8,
    color: theme.ColumnHeaderTextColor,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: theme.customMultiSelectColor,
    },
  },
  buttonRoot: {
    margin: 0,
    padding: 0,
    color: theme.iconColor,
    backgroundColor: "transparent",
    "& $buttonRootLabel": {
      color: theme.iconColor,
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  buttonActive: {
    "& $buttonRootLabel": {
      color: theme.HeaderSelectedText,
    },
  },
  buttonRootLabel: {},
  iconItem: {
    fontSize: theme.textFontSize,
    paddingRight: 5,
  },
  hideColumn: {
    // here we are hiding column using height 0 because display:"none" gives wrong destination index on DragEnd method
    visibility: "hidden",
    height: 0,
  },
  hiddenColumn: {
    opacity: 0.6,
  },
  popper: {
    // High Z-index to ensure menu appears above all headers and layout in maximized mode
    zIndex: 1200, // should be shown on top of everythign when clicked on show/hide columns button
  },
  noResults:{
    fontSize: theme.textFontSize,
    textAlign:"center",
    opacity: 0.6,
    color: theme.TextColor,
    padding: 8
  },
});

class MenuListComposition extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      columns: [],
      searchTerm: "",
      filteredColumns: []
    };
    this.portalItem = null;
  }

  componentDidMount() {
    this.portalItem = document.getElementById("columnDrag");
    if (!this.portalItem) {
      this.portalItem = document.createElement("div");
      this.portalItem.id = "columnDrag";
    }
    document.body.appendChild(this.portalItem);
  }

  componentWillUnmount() {
    if (this.portalItem) {
      this.portalItem.parentNode &&
        this.portalItem.parentNode.removeChild(this.portalItem);
      this.portalItem = null;
    }
  }

  shouldComponentUpdate(_nextProps, nextState){
      return this.state.open !== nextState.open || this.state.open;
  }

  handleToggle = () => {
    if (!this.state.open) {
      this.setFilteredColumns();
    }
    this.setState((state) => ({ open: !state.open }));
  };

  handleClose = (event) => {
    saveGridColumnState({
      gridName: this.props.api.gridName,
      columns: this.props.api.getAllGridColumns(),
      userColumnPref: this.props.userData.userProfile?.columnPref
    });

    if (this.anchorEl.contains(event.target)) {
      return;
    }
    this.setState({ open: false });
  };

  showHideColumn = (column) => {
    column.visible = !column.visible;
    if (this.props.api) {
      this.props.api.setColumnsVisible(
        [column.colId],
        column.visible
      );
      this.props.api.applyColumnState(
        this.props.api.getColumnState()
      );
      if (column.visible) {
        this.props.api.setColumnWidths(
          [{ [column.colId]: column.colDef.minWidth || 150 }],
        );
      }
    }
    this.forceUpdate();
  };

  findFirstMovableColumnIndex = () => {
    const columns = this.props.api.getColumns();
    return columns.findIndex((col) => !col.lockPosition);
  };

  moveColumn = (sourceIndex, destinationIndex) => {
    this.props.api.moveColumnByIndex(sourceIndex, destinationIndex);
  };

  onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    // If tried to move a column beyond locked columns, move it as the first movable column
    // This behaviour will be consistent with what we observe in grid
    const firstMovableColumn = this.findFirstMovableColumnIndex();
    let destinationIndex = result.destination.index;

    if (destinationIndex < firstMovableColumn) {
      destinationIndex = firstMovableColumn;
    }

    this.moveColumn(result.source.index, destinationIndex);
    this.setFilteredColumns();
  };

  getItemStyle = (draggableStyle, lockPosition) => {
    const transformPosition = lockPosition ? { transform: "0px" } : {}; // disabled moving of locked columns when a column is dragged over them
    return {
      ...draggableStyle,
      ...transformPosition,
    };
  };

  // the top value for the item is calculated with respect to the body, so rendering it as portal calculates the correct top value
  getItem = (column, provided, snapshot, classes) => {
    const item = (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`${
          column.colDef.isInvisibleInShowHide ? classes.hideColumn : ""
        }`}
        style={this.getItemStyle(
          provided.draggableProps.style,
          column.colDef.lockPosition
        )}
      >
        <MenuItem
          classes={{
            root: `${classes.menuItemText} ${
              column.visible ? "" : classes.hiddenColumn
            }`,
          }}
          onClick={() => this.showHideColumn(column)}
        >
          {column.visible ? (
            <Visibility classes={{ root: classes.iconItem }} />
          ) : (
            <VisibilityOff classes={{ root: classes.iconItem }} />
          )}
          {column.colDef.headerName}
        </MenuItem>
      </div>
    );

    if (snapshot.isDragging) {
      return ReactDOM.createPortal(item, this.portalItem);
    }
    return item;
  };

  setFilteredColumns(searchTerm = "") {
    const searchTermValue = searchTerm?.toLowerCase();
    const columns = this.props.api?.getAllGridColumns();
    
    if (!columns) return;
    
    if(!searchTermValue) {
      this.setState({ filteredColumns: columns, searchTerm: ""   });
    }else{
      const filteredColumns = columns.filter(({ colDef: { headerName = '' } }) =>
        headerName.toLowerCase().includes(searchTermValue)
      );
      this.setState({ filteredColumns, searchTerm: searchTermValue });
    }
  }

  handleSearch = (searchTerm) => {
    this.setFilteredColumns(searchTerm);
  }

  render() {
    const { classes } = this.props;
    const { open, searchTerm, filteredColumns } = this.state;

    // if searching is in progress, disable drag and drop
    const isDragDropDisabled = Boolean(searchTerm);

    return (
      <div className={classes.root}>
        <div>
          <Tooltip title="Show/Hide columns">
            <IconButton
              buttonRef={(node) => {
                this.anchorEl = node;
              }}
              aria-owns={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
              onClick={this.handleToggle}
              classes={{
                root: classes.buttonRoot,
                label: classes.buttonRootLabel,
              }}
              className={open ? classes.buttonActive : ''}>
              <Visibility />
            </IconButton>
          </Tooltip>
          <Popper
            open={open}
            anchorEl={this.anchorEl}
            transition
            placement="bottom-end"
            className={classes.popper}>
            {({ TransitionProps, placement }) => (
              <Grow {...TransitionProps} id="menu-list-grow">
                <Paper classes={{ root: classes.paper }}>
                  <ClickAwayListener onClickAway={this.handleClose}>
                    <div>
                      <ListSearch onSearch={this.handleSearch} />
                      {filteredColumns.length > 0 ? (
                        <MenuList classes={{ root: classes.menuList }}>
                          <DragDropContext onDragEnd={this.onDragEnd}>
                            <Droppable droppableId="droppable" isDropDisabled={isDragDropDisabled}>
                              {(provided) => (
                                <div ref={provided.innerRef}>
                                  {filteredColumns?.map((column, index) => {
                                    const headerId = `${column.colDef.headerName}_${index}`;
                                    return (
                                      <Draggable
                                        key={headerId}
                                        draggableId={
                                          column.colDef.headerName ? headerId : `draggableId_${index}`
                                        }
                                        index={index}
                                        isDragDisabled={
                                          column.colDef.lockPosition || isDragDropDisabled
                                        }>
                                        {(provided, snapshot) =>
                                          this.getItem(column, provided, snapshot, classes)
                                        }
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </MenuList>
                      ) : (
                        <div className={classes.noResults}>NO RESULTS</div>
                      )}
                    </div>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(MenuListComposition);
